import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Button,
  VStack,
  Input,
  Card,
  CardHeader,
  CardBody,
  useToast,
  Select,
  useBreakpointValue,
  Flex,
} from '@chakra-ui/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getGenericReportingData } from '../actions/reportingActions';
import { fetchCompanies } from '../actions/companyActions';
import { getApiDateRangeStringsForGenericPreset } from '../utils/reportDateRange';
import CascadingDropdownFilter from './CascadingDropdownFilter';
import salesReportTableStyles from '../styles/SalesReportTable.module.css';
import { getAuthHeaders } from '../utils/authHeaders';
import { useFieldPrivilege } from '../hooks/useFieldPrivilege';

const base_url = process.env.REACT_APP_API_URL_ADMIN;

const DATE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'currentWeek', label: 'Current Week' },
  { key: 'lastWeek', label: 'Last Week' },
  { key: 'currentMonth', label: 'Current Month' },
  { key: 'betweenDates', label: 'Between Dates' },
];

function idToQueryString(v) {
  if (v == null || v === '' || v === 'null' || v === 'undefined') return '';
  if (typeof v === 'object' && typeof v.toString === 'function') return v.toString();
  return String(v);
}

function queryParamClean(params, key) {
  const v = params.get(key);
  if (v == null || v === '' || v === 'null' || v === 'undefined') return '';
  return v;
}

/** Super-admin super-stockez: only drill-down id from URL (company/admin come from slug + username via resolver). */
function superAdminSuperStockezSelectionsFromURL(params) {
  const s = {};
  const ss = queryParamClean(params, 'superStockez');
  if (ss) s.super_stokez_id = ss;
  return s;
}

async function fetchAdminOptionsForCompany(companyId) {
  const response = await fetch(`${base_url}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      apiName: 'user',
      command: 'getOptions',
      level: 'admin_id',
      filters: { company_id: companyId },
    }),
  });
  const data = await response.json();
  return Array.isArray(data.options) ? data.options : [];
}

const DateFilter = ({ selectedFilter, onFilterChange, startDate, endDate, onDateChange, onViewSales }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <VStack spacing={4} align="stretch">
      {isMobile ? (
        <Select
          value={selectedFilter}
          onChange={(e) => onFilterChange(e.target.value)}
          width="100%"
          bg="white"
        >
          {DATE_PRESETS.map(({ key, label }) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </Select>
      ) : (
        <HStack spacing={0}>
          {DATE_PRESETS.map(({ key, label }) => (
            <Button
              key={key}
              flex={1}
              colorScheme={selectedFilter === key ? 'blue' : 'orange'}
              onClick={() => onFilterChange(key)}
            >
              {label}
            </Button>
          ))}
        </HStack>
      )}

      {selectedFilter === 'betweenDates' && (
        <Flex
          gap={3}
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'stretch', md: 'center' }}
        >
          <Input type="date" value={startDate} onChange={(e) => onDateChange('start', e.target.value)} />
          <Input type="date" value={endDate} onChange={(e) => onDateChange('end', e.target.value)} />
          <Button colorScheme="blue" onClick={onViewSales} width={{ base: '100%', md: 'auto' }}>
            View Sales
          </Button>
        </Flex>
      )}
    </VStack>
  );
};

const GenericReport = ({ type }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const location = useLocation();
  const { canShowField } = useFieldPrivilege();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [initialSelections, setInitialSelections] = useState({});

  const getSelectionsFromURL = (params) => {
    const selections = {};

    if (params.get('superStockez')) {
      selections.super_stokez_id = params.get('superStockez');
    }
    if (params.get('stockez')) {
      selections.stokez_id = params.get('stockez');
    }
    if (params.get('agent')) {
      selections.agent_id = params.get('agent');
    }

    return selections;
  };

  const { reportingData, loading, error } = useSelector((state) => state.reporting);
  const user = useSelector((state) => state.auth.user);
  const companies = useSelector((state) => state.company?.companies ?? []);
  const isSuperAdminSuperStockezUrlSync =
    user?.role === 'super_admin' && type === 'superStockez';

  const [selectedFilter, setSelectedFilter] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const ROLE_HIERARCHY = {
    superStockez: [
      { key: 'company_id', label: 'Company', role: 'company' },
      { key: 'admin_id', label: 'Admin', role: 'admin' },
      { key: 'super_stokez_id', label: 'Super Stokez', role: 'super_stokez' },

    ],
    stockez: [
      { key: 'company_id', label: 'Company', role: 'company' },
      { key: 'admin_id', label: 'Admin', role: 'admin' },
      { key: 'super_stokez_id', label: 'Super Stokez', role: 'super_stokez' },
      { key: 'stokez_id', label: 'Stokez', role: 'stokez' },

    ],
    agent: [
      { key: 'company_id', label: 'Company', role: 'company' },
      { key: 'admin_id', label: 'Admin', role: 'admin' },
      { key: 'super_stokez_id', label: 'Super Stokez', role: 'super_stokez' },
      { key: 'stokez_id', label: 'Stokez', role: 'stokez' },
      { key: 'agent_id', label: 'Agent', role: 'agent' },

    ],
    player: [
      { key: 'company_id', label: 'Company', role: 'company' },
      { key: 'admin_id', label: 'Admin', role: 'admin' },
      { key: 'super_stokez_id', label: 'Super Stokez', role: 'super_stokez' },
      { key: 'stokez_id', label: 'Stokez', role: 'stokez' },
      { key: 'agent_id', label: 'Agent', role: 'agent' },
      { key: 'player_id', label: 'Player', role: 'player' }

    ]
  };

  const ROLE_BEING_REPORTED = {
    superStockez: 'super_stokez',
    stockez: 'stokez',
    agent: 'agent',
    player: 'player',
  };

  const routeMap = { "superStockez": "stockez", "stockez": "agent", "agent": "player" }

  const slug = useMemo(() => {
    const pathParts = location.pathname.split('/');
    return pathParts[1] || 'root';
  }, [location.pathname]);

  useEffect(() => {
    if (isSuperAdminSuperStockezUrlSync) {
      setInitialSelections(superAdminSuperStockezSelectionsFromURL(queryParams));
    } else {
      const nextSelections = location.search ? getSelectionsFromURL(queryParams) : {};
      setInitialSelections(nextSelections);
    }

    dispatch({ type: 'CLEAR_REPORTING_DATA' });
    setSelectedFilter('today');
    const [dStart, dEnd] = getApiDateRangeStringsForGenericPreset('today');
    setStartDate(dStart);
    setEndDate(dEnd);
  }, [location.search, type, dispatch, queryParams, isSuperAdminSuperStockezUrlSync]);

  useEffect(() => {
    if (!isSuperAdminSuperStockezUrlSync) return;
    if (!companies.length) {
      dispatch(fetchCompanies());
    }
  }, [isSuperAdminSuperStockezUrlSync, companies.length, dispatch]);

  useEffect(() => {
    if (!isSuperAdminSuperStockezUrlSync) return;

    const companySlug = queryParamClean(queryParams, 'company');
    const adminUsername = queryParamClean(queryParams, 'admin');
    const ss = queryParamClean(queryParams, 'superStockez');
    const base = {};
    if (ss) base.super_stokez_id = ss;

    const merge = (extra) => setInitialSelections({ ...base, ...extra });

    if (!companySlug) {
      merge({});
      return;
    }

    const company = companies.find((c) => c.slug === companySlug);
    if (!company) {
      merge({});
      return;
    }

    const company_id = company._id.toString();
    if (!adminUsername) {
      merge({ company_id });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const options = await fetchAdminOptionsForCompany(company_id);
        const opt = options.find((o) => o.label === adminUsername);
        if (!cancelled) {
          if (opt) merge({ company_id, admin_id: opt.value });
          else merge({ company_id });
        }
      } catch {
        if (!cancelled) merge({ company_id });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdminSuperStockezUrlSync, location.search, companies, queryParams]);

  const needsCompanyForSuperAdmin =
    user?.role === 'super_admin' && !initialSelections?.company_id;

  useEffect(() => {
    if (!startDate || !endDate) return;
    if (needsCompanyForSuperAdmin) return;
    dispatch(getGenericReportingData({ FromDate: startDate, ToDate: endDate, roleFilter: initialSelections, type }));
  }, [dispatch, startDate, endDate, initialSelections, type, needsCompanyForSuperAdmin]);

  useEffect(() => {
    if (error) {
      toast({
        title: 'Error fetching data',
        description: error,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error, toast]);

  const handleDateChange = (dateType, value) => {
    if (dateType === 'start') setStartDate(value);
    else setEndDate(value);
  };

  const handleCascadingDropdownChange = (newSelections) => {
    setInitialSelections(newSelections);
    if (!isSuperAdminSuperStockezUrlSync) return;

    (async () => {
      const next = new URLSearchParams(location.search);
      next.delete('company_id');
      next.delete('admin_id');

      const cid = newSelections.company_id;
      const aid = newSelections.admin_id;
      const company = companies.find((c) => String(c._id) === String(cid));
      const companySlug = company?.slug;

      let adminUsername = '';
      if (cid && aid) {
        try {
          const options = await fetchAdminOptionsForCompany(String(cid));
          adminUsername =
            options.find((o) => String(o.value) === String(aid))?.label || '';
        } catch {
          adminUsername = '';
        }
      }

      if (companySlug) next.set('company', companySlug);
      else next.delete('company');
      if (adminUsername) next.set('admin', adminUsername);
      else next.delete('admin');

      const qs = next.toString();
      navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
    })();
  };

  const handleRowClick = (row) => {
    if (type === 'player') {
      return;
    }
    const targetrole = routeMap[type];
    if (!targetrole) {
      return;
    }

    const qs = new URLSearchParams();

    switch (type) {
      case 'superStockez':
        qs.set('superStockez', idToQueryString(row.userObjectId));
        break;
      case 'stockez': {
        const superId =
          queryParamClean(queryParams, 'superStockez') ||
          idToQueryString(initialSelections.super_stokez_id) ||
          idToQueryString(row.super_stokez_id);
        const stokezId = idToQueryString(row.userObjectId);
        if (!superId) {
          toast({
            title: 'Cannot open Agent report',
            description: 'Parent Super Stokez is missing. Refresh the page after the API update, or select filters in the dropdown.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        qs.set('superStockez', superId);
        qs.set('stockez', stokezId);
        break;
      }
      case 'agent': {
        const superId =
          queryParamClean(queryParams, 'superStockez') ||
          idToQueryString(initialSelections.super_stokez_id) ||
          idToQueryString(row.super_stokez_id);
        const stokezId =
          queryParamClean(queryParams, 'stockez') ||
          idToQueryString(initialSelections.stokez_id) ||
          idToQueryString(row.stokez_id);
        const agentId = idToQueryString(row.userObjectId);
        if (!superId || !stokezId) {
          toast({
            title: 'Cannot open Player report',
            description: 'Super Stokez or Stokez is missing for this row.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        qs.set('superStockez', superId);
        qs.set('stockez', stokezId);
        qs.set('agent', agentId);
        break;
      }
      default:
        return;
    }

    navigate(`/${slug}/reports/${targetrole}?${qs.toString()}`);
  };

  const reportRows = Array.isArray(reportingData) ? reportingData : [];

  const totalRow = useMemo(() => {
    return reportRows.reduce((acc, row) => ({
      Sale: acc.Sale + parseFloat(row.Sale || 0),
      Win: acc.Win + parseFloat(row.Win || 0),
      Comm: acc.Comm + parseFloat(row.Comm || 0),
      NTP: acc.NTP + parseFloat(row.NTP || 0),
      Optr: acc.Optr + parseFloat(row.Optr || 0)
    }), { Sale: 0, Win: 0, Comm: 0, NTP: 0, Optr: 0 });
  }, [reportRows]);

  const showUserID = canShowField('reportHierarchy', 'UserID', 'list');
  const showSale = canShowField('reportHierarchy', 'Sale', 'list');
  const showWin = canShowField('reportHierarchy', 'Win', 'list');
  const showComm = canShowField('reportHierarchy', 'Comm', 'list');
  const showNTP = canShowField('reportHierarchy', 'NTP', 'list');
  const showOptr = canShowField('reportHierarchy', 'Optr', 'list');
  const showAnyNumeric = showSale || showWin || showComm || showNTP || showOptr;
  // Keep first column (UserID/Total) when UserID visible or any numeric cols need a Total label
  const showFirstCol = showUserID || showAnyNumeric;

  const onFilterChange = (filter) => {
    setSelectedFilter(filter);
    const [nextStart, nextEnd] = getApiDateRangeStringsForGenericPreset(filter);
    if (nextStart && nextEnd) {
      setStartDate(nextStart);
      setEndDate(nextEnd);
    } else {
      setStartDate('');
      setEndDate('');
    }
  };

  return (
    <Card>
      <CardHeader>
        <Heading size="lg">{type} Report</Heading>
        <div>

        </div>
      </CardHeader>
      <CardBody>
        <CascadingDropdownFilter
          onSelectionChange={handleCascadingDropdownChange}
          initialValues={initialSelections}
          userHierarchy={ROLE_HIERARCHY[type]}
          roleBeingCreated={ROLE_BEING_REPORTED[type]}
        />
        <VStack spacing={6} align="stretch">
          <DateFilter
            selectedFilter={selectedFilter}
            onFilterChange={onFilterChange}
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            onViewSales={() => {
              if (startDate && endDate && !needsCompanyForSuperAdmin) {
                dispatch(getGenericReportingData({ FromDate: startDate, ToDate: endDate, roleFilter: initialSelections, type }));
              }
            }}

          />

          {needsCompanyForSuperAdmin ? (
            <Box>Please select a company to view this report.</Box>
          ) : loading ? (
            <Box>Loading...</Box>
          ) : error ? (
            <Box>Error: {error}</Box>
          ) : (
            <Box overflowX="auto" className={salesReportTableStyles.tableWrapper}>
              <Table variant="simple" size="sm" className={salesReportTableStyles.table}>
                <Thead>
                  <Tr>
                    {showFirstCol && (showUserID ? <Th>User ID</Th> : <Th />)}
                    {showSale && <Th isNumeric>Sale</Th>}
                    {showWin && <Th isNumeric>Win</Th>}
                    {showComm && <Th isNumeric>Commission</Th>}
                    {showNTP && <Th isNumeric>NTP</Th>}
                    {showOptr && <Th isNumeric>Optr</Th>}
                  </Tr>
                </Thead>
                <Tbody>
                  {reportRows.map((row) =>
                    type === 'player' ? (
                      <Tr key={row.UserID}>
                        {showFirstCol && <Td>{showUserID ? row.UserID : null}</Td>}
                        {showSale && (
                          <Td isNumeric>
                            <Link
                              to={`/${slug}/reports/tktdetails?UserID=${encodeURIComponent(row.UserID)}&TicketID=0&FDate=${startDate}&TDate=${endDate}`}
                              style={{ color: 'blue', textDecoration: 'underline' }}
                            >
                              {row.Sale}
                            </Link>
                          </Td>
                        )}
                        {showWin && <Td isNumeric>{row.Win}</Td>}
                        {showComm && <Td isNumeric>{row.Comm}</Td>}
                        {showNTP && <Td isNumeric>{row.NTP}</Td>}
                        {showOptr && <Td isNumeric>{row.Optr}</Td>}
                      </Tr>
                    ) : (
                      <Tr
                        key={row.UserID}
                        cursor="pointer"
                        onClick={() => handleRowClick(row)}
                        _hover={{ bg: 'gray.50' }}
                      >
                        {showFirstCol && <Td>{showUserID ? row.UserID : null}</Td>}
                        {showSale && <Td isNumeric>{row.Sale}</Td>}
                        {showWin && <Td isNumeric>{row.Win}</Td>}
                        {showComm && <Td isNumeric>{row.Comm}</Td>}
                        {showNTP && <Td isNumeric>{row.NTP}</Td>}
                        {showOptr && <Td isNumeric>{row.Optr}</Td>}
                      </Tr>
                    )
                  )}
                  <Tr fontWeight="bold" bg="gray.50">
                    {showFirstCol && <Td>Total</Td>}
                    {showSale && <Td isNumeric>{totalRow.Sale.toFixed(2)}</Td>}
                    {showWin && <Td isNumeric>{totalRow.Win.toFixed(2)}</Td>}
                    {showComm && <Td isNumeric>{totalRow.Comm.toFixed(2)}</Td>}
                    {showNTP && <Td isNumeric>{totalRow.NTP.toFixed(2)}</Td>}
                    {showOptr && <Td isNumeric>{totalRow.Optr.toFixed(2)}</Td>}
                  </Tr>
                </Tbody>
              </Table>
            </Box>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default GenericReport;
